import { useState } from 'react';
import { useSelector } from 'react-redux';
import { Box, Button, Modal, Select, Text } from '@rsos/base-components';
import {
  ModalBody,
  ModalFooter,
  ModalHeader,
} from '@rsos/base-components/Modal/ModalComponents';
import midasInstance from '@rsos/midas/src/midasInstance';
import { selectExistingRequest } from '@rsos/reduxDataStore';
import {
  trackCancelAirAmbulanceRequest,
  trackCancelAirAmbulanceRequestApiResponse,
} from '../../../gainsightTrackings/airAmbulanceTrackings';
import { CANCEL_REQUEST_REASONS } from '../AirAmbulanceForm/constants';
import { AIR_AMBULANCE_REASON_NO_EXT_TRIP_ID } from './constants';
import { handleCancelFailed } from './exceptions';

const AirAmbulanceControlsModal = ({ showModal, onClose, handleCancel }) => {
  const [cancelReason, setCancelReason] = useState(null);
  const existingRequest = useSelector(selectExistingRequest);
  const { first_name, last_name, email } = useSelector(
    state => state.sinatra.user.profile,
  );
  const isSubmitDisabled = !cancelReason;

  const onSelectChange = selectedOption => {
    setCancelReason(selectedOption);
  };

  const onConfirmCancel = async () => {
    const { request_id = null, ext_trip_id = null } =
      existingRequest?.body || {};

    if (!ext_trip_id) {
      handleCancelFailed(AIR_AMBULANCE_REASON_NO_EXT_TRIP_ID, { request_id });
      return;
    }

    trackCancelAirAmbulanceRequest({
      requestId: ext_trip_id,
      reason: cancelReason?.value,
    });

    const payload = {
      callflow: '/air_ambulance/gmr_cancel',
      variables: {
        ext_trip_id: ext_trip_id,
        cancel_reason: cancelReason?.value,
        change_requestor: {
          email,
          first: first_name,
          last: last_name,
        },
      },
    };
    try {
      const response = await midasInstance.post(
        '/internal_auth/trigger',
        payload,
        {
          headers: { retry: true },
        },
      );
      trackCancelAirAmbulanceRequestApiResponse({
        requestId: ext_trip_id,
        status: response?.status,
        error: false,
        success: response?.data?.detail,
      });
    } catch (error) {
      // Error cancelling request - this error is tracked via gainsight
      trackCancelAirAmbulanceRequestApiResponse({
        requestId: ext_trip_id,
        status: error?.response?.status,
        error: error?.message,
        success: false,
      });
    }
    // Callback
    handleCancel();
  };

  return (
    <Modal
      id="air-ambulance-cancel-request-modal"
      isOpened={showModal}
      onClose={onClose}
      padding="45px 45px 35px"
      width={400}
    >
      <ModalHeader onClose={onClose}>
        <Text color="primaryText" size="large">
          Cancel Air Ambulance Request?
        </Text>
      </ModalHeader>
      <ModalBody>
        <Text color="secondaryText" size="small">
          Are you sure you want to cancel the request for air ambulance?
        </Text>
        <Box mt={2}>
          <Select
            options={CANCEL_REQUEST_REASONS}
            value={cancelReason}
            onChange={onSelectChange}
            placeholder="Select a cancellation reason (required)"
          />
        </Box>
      </ModalBody>
      <ModalFooter>
        <Box horizontal justify="center" width="100%">
          <Button grow btnType="tertiary" onClick={onClose}>
            Keep Request
          </Button>
          <Button
            grow
            btnType="warning_tertiary"
            onClick={onConfirmCancel}
            disabled={isSubmitDisabled}
          >
            Cancel Request
          </Button>
        </Box>
      </ModalFooter>
    </Modal>
  );
};

export default AirAmbulanceControlsModal;
